var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import classnames from 'classnames';
import { BodyText } from '../BodyText';
import { Icon } from '../Icon';
import { keys } from '../../utilities/keyCodes';
import { CollapsibleContext, CollapsibleList, } from '../../internal';
export const SideNavItem = (_a) => {
    var props = __rest(_a, []);
    const { className } = props, rest = __rest(props, ["className"]);
    const SideNavItemClasses = classnames('SideNavItem', className, {
        'SideNavItem--active': props.active,
        'SideNavItem--disabled': props.disabled,
    });
    const sideNavItemContext = React.useContext(SideNavItemContext);
    if (props.collapsible || sideNavItemContext) {
        const realCollapsible = props.collapsible === true
            ? {
                expanded: false,
                animate: true,
                extractCollapsibleList: true,
            }
            : Object.assign(Object.assign({}, props.collapsible), { extractCollapsibleList: true });
        return (React.createElement(CollapsibleList.Item, { el: "li", collapsible: props.collapsible && realCollapsible, className: SideNavItemClasses, "data-anvil-component": "SideNavItem" },
            React.createElement(SideNavItemButton, Object.assign({}, rest))));
    }
    return (React.createElement("li", { className: SideNavItemClasses, "data-anvil-component": "SideNavItem" },
        React.createElement(SideNavItemButton, Object.assign({}, rest))));
};
/**
 * SideNav Item's Button
 */
const SideNavItemButton = (_a) => {
    var { active, className, collapsible, children, disabled, href, icon, prefix, suffix } = _a, rest = __rest(_a, ["active", "className", "collapsible", "children", "disabled", "href", "icon", "prefix", "suffix"]);
    const El = href ? 'a' : 'span';
    const buttonRef = React.useRef(null);
    const collapsibleContext = React.useContext(CollapsibleContext);
    const expanded = collapsible && (collapsibleContext === null || collapsibleContext === void 0 ? void 0 : collapsibleContext.expanded);
    const SideNavItemButtonClasses = classnames('SideNavItem__button', {
        'SideNavItem__button--expanded': expanded,
    });
    const handleKeyDown = (e) => {
        var _a;
        if (![keys.enter, keys.space].includes(e.key))
            return;
        if (e.target !== buttonRef.current) {
            e.stopPropagation(); // Disable higher handlers (collapse-expand) for buttons placed inside Item's suffix
            return;
        }
        if (e.key === keys.space)
            e.preventDefault(); // Prevent scroll on space keypress
        (_a = buttonRef === null || buttonRef === void 0 ? void 0 : buttonRef.current) === null || _a === void 0 ? void 0 : _a.click();
    };
    const renderPrefix = () => {
        const i = renderIcon(icon);
        if (!i && !prefix)
            return null;
        return (React.createElement("span", { className: "SideNavItem__button__prefix" },
            i,
            prefix));
    };
    const renderSuffix = () => {
        const arrow = collapsible ? (React.createElement(Icon, { className: "SideNavItem__button__suffix__collapsible-icon", name: "keyboard_arrow_down", size: 20 })) : null;
        if (!suffix && !arrow)
            return null;
        return (React.createElement("span", { className: "SideNavItem__button__suffix" },
            suffix,
            arrow));
    };
    return (React.createElement(SideNavItemContext.Provider, { value: { buttonRef } },
        React.createElement(El, Object.assign({}, rest, { className: SideNavItemButtonClasses, href: href, tabIndex: disabled ? -1 : 0, target: href && rest.target, ref: buttonRef, onKeyDown: handleKeyDown, "aria-current": active && !expanded ? 'page' : null, "aria-disabled": disabled ? true : null }),
            renderPrefix(),
            React.createElement(BodyText, { el: "span", size: "small", className: "SideNavItem__button__content" }, children),
            renderSuffix())));
};
export const SideNavItemContext = React.createContext(null);
const renderIcon = (icon) => {
    // Icon passed as Icon name
    if (typeof icon === 'string') {
        return (React.createElement(Icon, { className: "SideNavItem__button__icon", name: icon, size: 20 }));
    }
    // Icon passed as an IconProps object
    if (icon != null && !React.isValidElement(icon)) {
        return (React.createElement(Icon, Object.assign({ className: classnames('SideNavItem__button__icon', icon.className) }, icon)));
    }
    return null;
};
//# sourceMappingURL=SideNavItem.js.map